import React from 'react';
import { get, isUndefined, isEmpty, merge, toNumber, isNull } from 'lodash';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';

// local dependencies
import { shipppingAndPayment as ShipppingAndPaymentView } from 'ui/views/widgets/shipppingAndPayment';
import { ShipppingAndPaymentService } from 'services/shipppingAndPayment';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import * as confirmOverlay from 'utils/confirmOverlay';
import { getLabel } from 'utils/getLabel';
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';
import { getBackRouteForCheckoutResolver } from 'resolvers/getBackRouteForCheckout';

// get store
const store = getStore();

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { ShipppingAndPayment } from 'ui/containers/ShipppingAndPayment';
 *
 * // render
 * <ShipppingAndPayment propName='propValue'/>
 */
class _ShipppingAndPayment extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        const queryParams = queryString.parse( this.props.location.search );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            assignedCoverage: null,
            enableNextForDavita: false,
            shippingAddress: {},
            shippingOption: {},
            costSelection: {},
            paymentOption: {},
            isValid: true,
            requestPayload: undefined,
            zipCode:null,
            origin: get( queryParams, 'origin', 'memberManagement' )
        };

        // create service instance
        this.shipppingAndPaymentService = new ShipppingAndPaymentService();

        // bind
        this._bind();
        
        // start data fetch
        this.fetchComponentData();
    }

    /*********************************************/

    /**
     * @des: bind functions
     */
    _bind() {
        this.handleNavActionClick = this.handleNavActionClick.bind( this );
        this.handleCheckoutSelection = this.handleCheckoutSelection.bind( this );
        this.submitStepData = this.submitStepData.bind( this );
        this.cancelOrder = this.cancelOrder.bind( this );
        this.moveToPreviousStep = this.moveToPreviousStep.bind( this );
    }

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'ShipppingAndPayment.fetchComponentData()' );

        // cancel ongoing request
        this.shipppingAndPaymentService.cancel();

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const memberId = get( this.props.requestPayload, 'context.memberId', undefined );
        const patientId = get( this.props.requestPayload, 'context.patientId', undefined );

        // fetch component data
        const { status, data } = await this.shipppingAndPaymentService.fetchComponentData( {
            url: this.props.widget.url,
            data: {
                context: {
                    callId,
                    callType,
                    memberId,
                    patientId
                    
                }
            }
        } );
        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {

            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
                requestPayload: {
                    context: {
                        callId,
                        callType,
                        memberId,
                        patientId
                    }
                }
            } );
           
        }
    }

     /**
     * @desc move to next step and selection data for current step.
     */
    async submitStepData( { url, data } ) {
        log.debug( 'ShipppingAndPayment.submitStepData()' );
        this.shipppingAndPaymentService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status } = await this.shipppingAndPaymentService.submitShippingAndPaymentInfo( {
            url: url,
            data: merge( data, {
                ...this.state.requestPayload,
               
            } ),
        } );
    

        // hide page level preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // move to the next step
            this.props.moveForward();

        } else {
            errorOverlay.open(); // show error
        }
    }

     /**
     * @desc move to previous step.
     */
    async moveToPreviousStep( { url } ) {
        log.debug( 'ShipppingAndPayment.moveToPreviousStep()' );

        this.shipppingAndPaymentService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status } = await this.shipppingAndPaymentService.moveToPreviousStep( {
            url: url,
            data: {
                ...this.state.requestPayload
            },
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // move to the next step
            this.props.moveBackward();

        } else {
            errorOverlay.open(); // show error
        }
    }

    /**
     * cancel order
     */
    async cancelOrder( { action } ) {
        log.debug( 'ShipppingAndPayment.cancelOrder()' );

        const shouldProceed = await confirmOverlay.open( {
			message: getLabel( action.confirmation ),
			iconName: 'close'
        } );
        
        if( shouldProceed ) {

            this.shipppingAndPaymentService.cancel();

            // show page level preloader
            pageLevelPreloader.show();
    
            // get status and service data
            const { status } = await this.shipppingAndPaymentService.cancelOrder( {
                url: action.url,
                data: {
                    ...this.state.requestPayload
                }
            } );
    
            // hide page level preloader
            pageLevelPreloader.hide();
    
            if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

                // const backRoutePayload = backRoute.decode( this.props.location );

                // if( ! isEmpty( backRoutePayload ) ) {
                //     return this.props.history.push( backRoutePayload );
                // } else {
                //     this.props.history.goBack();
                // }

                const queryParams = queryString.parse( this.props.location.search );
                const queryState = get( this.props.location, 'state', {} );

                const checkoutBackRoute = getBackRouteForCheckoutResolver( { queryParams, queryState } );
                this.props.history.push( checkoutBackRoute );
    
                // move to the next step
                // this.props.history.goBack();
    
            } else {
                errorOverlay.open(); // show error
            }
        }
    }

    /**
     * @desc: action executed when user clicks on any of the navigation actions
     */
    handleNavActionClick( { value } ) {
        log.debug( 'ShipppingAndPayment.handleNavActionClick()' );

        if( 'cancel' === value.name ) {

            this.cancelOrder( { action: value } );
        }

        if( 'prev' === value.name ) {
            this.moveToPreviousStep( { url: value.url } );
        }

        if( 'next' === value.name ) {

            // check if payment is required
            // const validCostSelection = pickBy( this.state.costSelection, ( _value = {} ) => ( ! isNil( _value ) && 0 < toNumber( _value ) ) );
            // const isPaymentRequired = 0 < toNumber( get( this.state, 'costSelection.chargeNowAmount', 0 ) );

            const data = {
                assignedCoverage: get( this.state, 'assignedCoverage', null ),
                shippingAddress: get( this.state, 'shippingAddress.id',  null ),
                shippingOption: get( this.state, 'shippingOption.id',  null ),
                estimatedDelivery: get( this.state, 'shippingOption.estDeliveryDate',  null ),
                costSelection: {
                    balancePayment: get( this.state, 'costSelection.balancePayment', 0 ),
                    orderPayment: get( this.state, 'costSelection.orderPayment', 0 ),
                    payOtherAmount: get( this.state, 'costSelection.payOtherAmount', 0 ),
                    payOther: get( this.state, 'costSelection.payOther', false ),
                    payLater: get( this.state, 'costSelection.payLater', false ),
                    payAch: get( this.state, 'costSelection.payAch', false )
                },
                paymentOption: get( this.state, 'paymentOption.id', null )
            };

            this.submitStepData( {
                url: value.url,
                data: { data }
            } );
        
        }
    }

    /**
     * @desc: action executed when MSR updates a selection for checkout process
     */
    handleCheckoutSelection( { type, item, isValid } ) {
        log.debug( 'ShipppingAndPayment.handleCheckoutSelection()' );

        if ( 'coverage' === type ) {
            this.setState( { assignedCoverage: item } );
        }

        if( 'address' === type ) {
            this.setState( { shippingAddress: item } );
        }

        if( 'shippingOption' === type ) {
            this.setState( { shippingOption: item } );
        }

        if( 'costSelection' === type )  {
            this.setState( { costSelection: item, isValid: isValid } );
        }

        if( 'paymentOption' === type )  {
            this.setState( { paymentOption: item } );
        }

    }

    /**
     * @desc Evaluate isPaymentRequired flag.
     */
     evaluatePaymentRequiredForAch() {
        log.debug( 'ShipppingAndPayment.evaluatePaymentRequiredForAch()' );

        let isPaymentRequired = true;

        if ( 0 >= toNumber( get( this.state, 'costSelection.balancePayment', 0 ) ) ) {

            isPaymentRequired = false;

        } else {

            if ( get( this.state, 'costSelection.payOther', false ) && 0 >= toNumber( get( this.state, 'costSelection.payOtherAmount', 0 ) ) ) {

                isPaymentRequired = false;
            }
        }

        return isPaymentRequired;

    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.shipppingAndPaymentService.cancel(); // cancel ongoing requests
    }
    
    updateEnableNextForDavita = (val) => {
        this.setState( {enableNextForDavita: val} )
    }

    /**
     * @desc Render a React view component
     */
    render() {

        // get the shipping charges
        const shippingOption = get( this.state, 'shippingOption', undefined );
        const shippingCharges = ! isUndefined( shippingOption ) ? shippingOption.additionalCost: null;

        // check if payment is required
        // const validCostSelection = pickBy( this.state.costSelection, ( value = {} ) => ( ! isNil( value ) && 0 < toNumber( value ) ) );
        let isPaymentRequired = ( ! get( this.state, 'costSelection.payLater' ) || 0 < toNumber( get( this.state, 'costSelection.chargeNowAmount', 0 ) ) );

        // If PayAch selected, payment should be disabled
        if ( get( this.state, 'costSelection.payAch', false ) ) {

            isPaymentRequired = this.evaluatePaymentRequiredForAch();
        }

        // can move forward
        let canMoveForward = true;

        if( isEmpty( this.state.shippingOption ) ||
        
        isEmpty( this.state.shippingAddress ) ||
        
        isEmpty( this.state.costSelection ) ||
        
        ( ! this.state.isValid ) ||
        
        ( isPaymentRequired && isEmpty( this.state.paymentOption ) ) ||
        
            isNull( this.state.assignedCoverage ) ) {

            canMoveForward = false;

        }
        //For davita payment is disabled need to remove if rrequired
        if(this.state.enableNextForDavita ) {
         canMoveForward = true;

        }
        return (
            <ShipppingAndPaymentView
                isFetching={ this.state.isFetching }
                updateZip= { (val)=>{this.setState({zipCode: val})} }
                zipCode ={ this.state.zipCode }
                networkStatus={ this.state.networkStatus }
                { ...get( this.state, 'componentData', {} ) }
                onNavActionClick = { this.handleNavActionClick }
                updateCheckoutSelection = { this.handleCheckoutSelection }
                shippingCharges= { shippingCharges }
                isPaymentRequired={ isPaymentRequired } 
                canMoveForward={ canMoveForward }
                requestPayload={ this.state.requestPayload }
                stepIndex={ this.props.stepIndex }
                origin= { this.state.origin }
                selectedPaymentOptionId={ get( this.state.paymentOption, 'id', null ) }
                updateEnableNextForDavita={ this.updateEnableNextForDavita }
            />
        );
    }
}

// set display name
_ShipppingAndPayment.displayName = 'ShipppingAndPayment';

// set default props
_ShipppingAndPayment.defaultProps = {};

// prop types
_ShipppingAndPayment.propTypes = {};


export const ShipppingAndPayment = withRouter( _ShipppingAndPayment );
